import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, money } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const [tx, setTx] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (attempts.current >= 8) { setStatus("pending"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setTx(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        className="w-full max-w-xl text-center"
        data-testid="payment-success-page"
      >
        {status === "checking" && (
          <>
            <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Confirming</p>
            <h1 className="font-serif-lux mt-4 text-5xl font-light text-[#f5f5f0]">Securing your piece…</h1>
            <p className="mt-4 text-sm font-light text-[#a1a1aa]">Please hold while we verify the acquisition.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Acquired</p>
            <h1 data-testid="success-heading" className="font-serif-lux mt-4 text-5xl font-light leading-tight text-[#f5f5f0] md:text-6xl">
              It is yours,<br />and only yours.
            </h1>
            {tx?.product_title && (
              <p className="mt-6 text-base font-light text-[#a1a1aa]">
                {tx.product_title} — {money(tx.amount || 0)}
              </p>
            )}
            <div className="mt-12 flex justify-center gap-4">
              <Link to="/account" data-testid="view-collection-btn" className="hover-invert rounded-none border border-[#f5f5f0] bg-[#f5f5f0] px-8 py-4 text-[11px] uppercase tracking-[0.3em] text-black hover:bg-transparent hover:text-[#f5f5f0]">
                View my collection
              </Link>
              <Link to="/marketplace" className="rounded-none border border-white/25 px-8 py-4 text-[11px] uppercase tracking-[0.3em] text-[#f5f5f0] hover:border-[#f5f5f0]">
                Continue
              </Link>
            </div>
          </>
        )}
        {status === "pending" && (
          <>
            <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Processing</p>
            <h1 className="font-serif-lux mt-4 text-4xl font-light text-[#f5f5f0]">Payment is processing</h1>
            <p className="mt-4 text-sm font-light text-[#a1a1aa]">This is taking a moment. Your collection will update once confirmed.</p>
            <Link to="/account" className="mt-10 inline-block text-[11px] uppercase tracking-[0.3em] text-gold underline underline-offset-8">Go to account</Link>
          </>
        )}
        {status === "error" && (
          <>
            <p className="text-[11px] uppercase tracking-[0.4em] text-red-400">Not completed</p>
            <h1 className="font-serif-lux mt-4 text-4xl font-light text-[#f5f5f0]">Acquisition not completed</h1>
            <Link to="/marketplace" className="mt-10 inline-block text-[11px] uppercase tracking-[0.3em] text-gold underline underline-offset-8">Return to the collection</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
